import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Button, Column, InputJob } from '~/commonComponents';
import { Field, Value } from '~/commonComponents/InputJob/Field';
import BriefcaseIcon from '~/images/briefcase.svg';
import { TBreakTime, TWorkingInterviewFormData, WorkingInterviewFormRules } from './FormRule';
import { useFocusField } from './useFieldFocus';

export enum WorkingInterviewFormFields {
  AssistedHygiene = 'ASSISTED_HYGIENE',
  Dates = 'DATES',
  HourlyRate = 'HOURLY_RATE',
  Hours = 'HOURS',
  MealBreak = 'MEAL_BREAK',
  Specialty = 'SPECIALTY',
}

interface IWorkingInterviewFormProps {
  jobTitle: string;
  office: string;
  profession: string;
  defaultValues?: TWorkingInterviewFormData;
  isEdit?: boolean;
  readonlyFields?: WorkingInterviewFormFields[];
  unavailableDates?: Date[];
  onSubmit(formData: TWorkingInterviewFormData): void;
}

export const WorkingInterviewForm = ({
  defaultValues,
  isEdit = false,
  jobTitle,
  office,
  profession,
  readonlyFields = [],
  unavailableDates = [],
  onSubmit,
}: IWorkingInterviewFormProps) => {
  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
    setValue,
  } = useForm<TWorkingInterviewFormData>({
    defaultValues,
    mode: 'all',
    resolver: zodResolver(WorkingInterviewFormRules),
  });

  const { focus, handleFieldFocus } = useFocusField<WorkingInterviewFormFields>();

  const readonly = new Set(readonlyFields);
  const watchBreakTime = useWatch({ control, name: 'breakTime' });
  const watchHoursArrival = useWatch({ control, name: 'hoursArrival' });
  const watchHoursDeparture = useWatch({ control, name: 'hoursDeparture' });
  const watchIsBreakPaid = useWatch({ control, name: 'isPaidBreakTime' });

  const handleSetHours = (arrival: string, departure: string) => {
    setValue('hoursArrival', arrival, { shouldValidate: true, shouldDirty: true });
    setValue('hoursDeparture', departure, { shouldValidate: true, shouldDirty: true });
    handleFieldFocus(WorkingInterviewFormFields.MealBreak);
  };

  return (
    <Column gap={8} marginInline="auto" width="100%">
      <InputJob.SelectOffice isDisabled value={office} />

      <Field
        IconSrc={BriefcaseIcon}
        isDisabled
        title="Job Title:"
        ValueComponent={() => <Value style={{ maxWidth: 'auto' }} text={jobTitle} />}
      />

      <Controller
        control={control}
        name="dates"
        render={({ field }) => (
          <InputJob.Date
            {...field}
            excluded={unavailableDates}
            isDisabled={readonly.has(WorkingInterviewFormFields.Dates)}
            isOpen={focus === WorkingInterviewFormFields.Dates}
            onToggle={handleFieldFocus(WorkingInterviewFormFields.Dates)}
          />
        )}
      />

      <InputJob.Professional isDisabled value={profession} />

      <Controller
        control={control}
        name="specialty"
        render={({ field }) => (
          <InputJob.Specialty
            {...field}
            isDisabled={readonly.has(WorkingInterviewFormFields.Specialty)}
            isOpen={focus === WorkingInterviewFormFields.Specialty}
            professional={profession}
            onToggle={handleFieldFocus(WorkingInterviewFormFields.Specialty)}
          />
        )}
      />

      <Controller
        control={control}
        name="isAssistedHygiene"
        render={({ field }) => (
          <InputJob.AssistedHygiene
            {...field}
            isDisabled={readonly.has(WorkingInterviewFormFields.AssistedHygiene)}
            isOpen={focus === WorkingInterviewFormFields.AssistedHygiene}
            onToggle={handleFieldFocus(WorkingInterviewFormFields.AssistedHygiene)}
          />
        )}
      />

      <InputJob.Hours
        isDisabled={readonly.has(WorkingInterviewFormFields.Hours)}
        isOpen={focus === WorkingInterviewFormFields.Hours}
        valueArrival={watchHoursArrival}
        valueDeparture={watchHoursDeparture}
        onChange={handleSetHours}
        onToggle={handleFieldFocus(WorkingInterviewFormFields.Hours)}
      />

      <InputJob.MealBreak
        isDisabled={readonly.has(WorkingInterviewFormFields.MealBreak)}
        isOpen={focus === WorkingInterviewFormFields.MealBreak}
        valueBreak={watchBreakTime || undefined}
        valueIsPaid={watchIsBreakPaid}
        onToggle={handleFieldFocus(WorkingInterviewFormFields.MealBreak)}
        onChange={(key, value) => {
          if (key === 'break')
            setValue('breakTime', value as TBreakTime, {
              shouldValidate: true,
              shouldDirty: true,
            });
          if (key === 'is_paid')
            setValue('isPaidBreakTime', value as boolean, {
              shouldValidate: true,
              shouldDirty: true,
            });
        }}
      />

      <Controller
        control={control}
        name="hourlyRate"
        render={({ field }) => (
          <InputJob.HourlyRate
            {...field}
            professional={profession}
            isOpen={focus === WorkingInterviewFormFields.HourlyRate}
            onToggle={handleFieldFocus(WorkingInterviewFormFields.HourlyRate)}
          />
        )}
      />

      <Button
        disabled={isEdit ? !isDirty || !isValid : !isValid}
        text={isEdit ? 'Save Changes' : 'Next'}
        style={{ marginInline: 'auto', width: 296 }}
        onClick={handleSubmit(onSubmit)}
      />
    </Column>
  );
};
